// Dependencies
import React from 'react';
import { useRouter } from 'next/router';

// Material UI Components
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';

// Config
import { allLangs } from '../../../config';

// components
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { useDispatch, useSelector } from 'src/store/store';
import Iconify from 'src/components/Iconify';
import { changeLanguageWithNetwork, changePreferencesItem } from 'src/store/slices/auth';

// ----------------------------------------------------------------------
export default function LanguagePopover() {
  // ====================================
  // Redux
  const stateLanguage = useSelector((state) => state.auth.preferences?.language || 'en');

  // ====================================
  // State
  const [open, setOpen] = React.useState<HTMLElement | null>(null);

  // ====================================
  // Hooks
  const router = useRouter();
  const authState = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // ====================================
  // Handlers
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };
  const chooseLang = (lang: string) => {
    const languageObj = allLangs.find((option) => option.value === lang);
    if (languageObj) {
      if (authState.isAuthorization) {
        if (!authState.required) {
          dispatch(changePreferencesItem('language', languageObj.value));
        } else {
          dispatch(changeLanguageWithNetwork(languageObj.value));
        }
      } else {
        dispatch(changeLanguageWithNetwork(languageObj.value));
      }
    }
    setOpen(null);
  };

  // ====================================
  // Memos
  const currentLanguage = React.useMemo(
    () => allLangs.find((option) => option.value === stateLanguage) || allLangs[0],
    [stateLanguage]
  );

  // ====================================
  // Change language by state value
  React.useEffect(() => {
    if (stateLanguage !== router.locale) {
      router.push(router.pathname, router.asPath, { locale: stateLanguage });
    }
  }, [stateLanguage, router]);

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          width: 40,
          height: 40,
          ...(open && { bgcolor: 'action.selected' }),
        }}
      >
        <Iconify icon="tabler:language" />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={() => setOpen(null)}
        sx={{
          mt: 1.5,
          ml: 0.75,
          width: 'auto',
          '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 },
        }}
        disabledArrow
      >
        <Stack spacing={0.75}>
          {allLangs.map((option) => (
            <MenuItem
              selected={option.value === currentLanguage.value}
              onClick={() => chooseLang(option.value)}
              key={option.value}
              disabled={option.value === currentLanguage.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>
      </MenuPopover>
    </>
  );
}
